import React, { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import UserInfoPopup from "./UserInfoPopup";
import CreateUserPopup from "./CreateUserPopup";
import { CiUnlock, CiLock } from "react-icons/ci";
import { disableUser, enableUser, getUsers } from "../services/userServices";
import EditUserPopup from "./EditUserPopup";
import { useAuth } from "../context/AuthContextProvider";
import { useQuery } from "@tanstack/react-query";
import MigrateModal from "../components/MigrateModal";
import Toggle from "../components/Toggle";
import LoadingScreen from "../components/LoadingScreen";
import NoUser from "./NoUser";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

const Users = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [showOptions, setShowOptions] = useState(null);
  const [createUserVisible, setCreateUserVisible] = useState(false);
  const [editUserVisible, setEditUserVisible] = useState(false);
  const [userId, setUserId] = useState("");
  const { user, isModalOpen, globalProfile, setEditId, editId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState("");

  const permissions = user.permissionList || [];

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const getAllUsers = async () => {
    setLoading(true);
    try {
      const res = await getUsers();
      setLoading(false);
      return res;
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
      setLoading(false);
    }
  };

  const query = useQuery({
    queryKey: ["fetchAllUsers", globalProfile],
    queryFn: getAllUsers,
  });

  useEffect(() => {
    if (createUserVisible || editUserVisible || selectedUser) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [createUserVisible, editUserVisible, selectedUser]);

  const handleEnableUser = async (id) => {
    try {
      const res = await enableUser(id);
      toast.success(res.message);
      query.refetch();
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  const handleDisableUser = async (id) => {
    try {
      const res = await disableUser(id);
      toast.success(res.message);
      query.refetch();
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  const handleUserClick = (u) => {
    setSelectedUser(u);
    setShowOptions(null);
    setCreateUserVisible(false);
    setEditUserVisible(false);
    setEditId(u.id);
  };

  const handleEditClick = (id) => {
    setShowOptions(null);
    setEditUserVisible(true);
    setUserId(id);
    setSelectedUser(null);
    setCreateUserVisible(false);
  };

  const handleClosePopup = () => {
    setSelectedUser(null);
    setShowOptions(null);
    setCreateUserVisible(false);
    setEditUserVisible(false);
  };

  const handleSvgClick = (uid, event) => {
    event.stopPropagation();
    setShowOptions((prev) => (prev === uid ? null : uid));
  };

  const handleNewUserClick = () => {
    setCreateUserVisible(true);
    setSelectedUser(null);
    setShowOptions(null);
    setEditUserVisible(false);
  };

  const handleSearchChange = debounce((e) => {
    setOrderId(e.target.value || "");
  }, 300);

  const userList = useMemo(() => {
    const list = query.data?.data || [];
    if (!orderId) return list;
    const term = orderId.toLowerCase();
    return list.filter(
      (u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.phoneNumber?.toLowerCase().includes(term) ||
        u.role?.toLowerCase().includes(term)
    );
  }, [query.data?.data, orderId]);

  if (loading) {
    return <LoadingScreen title="User's List" />;
  }

  if (query.data?.data?.length === 0) {
    return <NoUser />;
  }

  return (
    <main className="overflow-hidden">
      <div className="flex gap-4">
        <div className="w-full">
          <div className="md:flex md:justify-between mb-6">
            <h1 className="text-2xl font-bold text-navy-blue md:mb-0 mb-2 ">
              User's List
            </h1>
            <Toggle />
          </div>

          <div className="md:flex items-center justify-between mb-4">
            <div className="relative mt-5 sm:m-0">
              <input
                type="text"
                placeholder="Search Users"
                onChange={handleSearchChange}
                className="xl:w-[557.5px] w-full h-[40px] rounded-[10px] px-2 border border-gray-300 focus:outline-none"
              />
              <span>
                <svg
                  className="absolute right-8 top-3"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g opacity="0.5">
                    <path
                      d="M7.22235 13.4445C10.6589 13.4445 13.4447 10.6587 13.4447 7.22223C13.4447 3.78579 10.6589 1 7.22235 1C3.78584 1 1 3.78579 1 7.22223C1 10.6587 3.78584 13.4445 7.22235 13.4445Z"
                      stroke="#030229"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15.0001 15.0005L11.6167 11.6172"
                      stroke="#030229"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </span>
            </div>
            <div className="p-4">
              {permissions.includes("Create User") && (
                <button
                  className="bg-text-orange text-sm font-semibold w-[130px] h-[40px] rounded-md text-white"
                  onClick={handleNewUserClick}
                >
                  + New User
                </button>
              )}
            </div>
          </div>

          <div
            className={`w-full md:max-w-full max-w-[340px] ${
              selectedUser ? "w-[70%]" : ""
            } bg-white rounded-lg shadow-sm`}
          >
            <DataTable
              value={userList}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 20]}
              emptyMessage="No users found."
              onRowClick={(e) => handleUserClick(e.data)}
              rowClassName={() => "cursor-pointer hover:bg-gray-50 transition-colors"}
              className="p-datatable-sm"
              responsiveLayout="scroll"
            >
              <Column
                field="firstName"
                header="Name"
                sortable
                body={(u) => (
                  <div className="py-2 font-medium text-slate-800">
                    {u.firstName} {u.lastName}
                  </div>
                )}
              />
              <Column
                field="email"
                header="Email"
                sortable
                body={(u) => <div className="py-2 text-slate-600">{u.email}</div>}
              />
              <Column
                field="phoneNumber"
                header="Phone Number"
                sortable
                body={(u) => <div className="py-2 text-slate-600">{u.phoneNumber}</div>}
              />
              <Column
                field="role"
                header="Role"
                sortable
                body={(u) => <div className="py-2 text-slate-700">{u.role}</div>}
              />
              <Column
                field="isDisabled"
                header="Status"
                sortable
                body={(u) => (
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      u.isDisabled
                        ? "bg-red-100 text-red-600"
                        : "bg-green-100 text-green-600"
                    }`}
                  >
                    {u.isDisabled ? "Inactive" : "Active"}
                  </span>
                )}
              />
              <Column
                header="Actions"
                body={(u) => (
                  <div
                    className="relative py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="cursor-pointer p-1"
                      onClick={(event) => handleSvgClick(u.id, event)}
                    >
                      <svg
                        width="20"
                        height="8"
                        viewBox="0 0 19 4"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.3">
                          <path
                            d="M13.4874 0.512567C12.804 -0.170848 11.696 -0.170848 11.0126 0.512567C10.3291 1.19598 10.3291 2.30402 11.0126 2.98744C11.696 3.67085 12.804 3.67085 13.4874 2.98744C14.1709 2.30405 14.1709 1.19601 13.4874 0.512567Z"
                            fill="#030229"
                          />
                          <path
                            d="M8.23744 0.512567C7.55402 -0.170848 6.44598 -0.170848 5.76257 0.512567C5.07915 1.19598 5.07915 2.30402 5.76257 2.98744C6.44598 3.67085 7.55402 3.67085 8.23744 2.98744C8.92085 2.30405 8.92085 1.19601 8.23744 0.512567Z"
                            fill="#030229"
                          />
                          <path
                            d="M2.98744 0.512567C2.30402 -0.170848 1.19598 -0.170848 0.512564 0.512567C-0.170852 1.19598 -0.170852 2.30402 0.512564 2.98744C1.19598 3.67085 2.30402 3.67085 2.98744 2.98744C3.67085 2.30405 3.67085 1.19601 2.98744 0.512567Z"
                            fill="#030229"
                          />
                        </g>
                      </svg>
                    </div>

                    {showOptions === u.id && (
                      <div className="bg-white border absolute right-0 top-8 z-50 flex flex-col gap-2 items-center p-2 w-[110px] rounded-[10px] shadow-md">
                        {permissions.includes("Update User") && (
                          <button
                            className="flex items-center gap-2 pl-2 w-[90px] h-[27px] text-[#5B93FF] rounded-[5px] font-[Nunito] text-xs bg-[#5B93FF]/15"
                            onClick={() => handleEditClick(u.id)}
                          >
                            Edit
                          </button>
                        )}
                        {u.isDisabled ? (
                          permissions.includes("Enable User") && (
                            <button
                              className="flex items-center gap-2 pl-2 w-[90px] h-[27px] text-[#5B93FF] font-[Nunito] rounded-[5px] text-xs bg-[#5B93FF]/15"
                              onClick={() => handleEnableUser(u.id)}
                            >
                              <CiUnlock /> Enable
                            </button>
                          )
                        ) : (
                          permissions.includes("Disable User") && (
                            <button
                              className="flex items-center gap-2 pl-2 w-[90px] h-[27px] text-[#E71D36] font-[Nunito] rounded-[5px] text-xs bg-[#E71D36]/15"
                              onClick={() => handleDisableUser(u.id)}
                            >
                              <CiLock /> Disable
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}
              />
            </DataTable>
          </div>
        </div>
        {isModalOpen && <MigrateModal />}

        {selectedUser && (
          <div className="z-50 fixed inset-0 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full">
              <UserInfoPopup
                user={selectedUser}
                onClose={handleClosePopup}
                handleEdit={() => handleEditClick(editId)}
              />
            </div>
          </div>
        )}

        {createUserVisible && (
          <div className="z-50 fixed inset-0 xl:w-[111%] overflow-y-auto bg-black bg-opacity-50 flex items-center justify-end shadow-lg">
            <div className="rounded-lg shadow-lg max-w-lg w-full">
              <CreateUserPopup
                onClose={() => setCreateUserVisible(false)}
                setCreate={setCreateUserVisible}
              />
            </div>
          </div>
        )}

        {editUserVisible && (
          <div className="z-50 fixed inset-0 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full">
              <EditUserPopup
                onClose={() => setEditUserVisible(false)}
                userId={userId}
              />
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default Users;
